// Covers UC-170 · AC-170-01, AC-170-04, AC-170-05, AC-170-08, AC-170-09,
// AC-170-10, AC-170-11, AC-170-12, AC-170-14

export const IMPORT_CONFLICT_TYPE = '/errors/variant-mapping/import-conflict';
export const REMAP_REQUIRED_TYPE = '/errors/variant-mapping/remap-confirmation-required';

export interface ExistingIdentifierLink {
  identifierType: string;
  identifierValue: string;
  masterProductId: string;
  displayName?: string;
}

export interface MappingCsvRow {
  cells: string[];
}

export interface MappingObservation {
  rowIndex: number;
  identifierType: string;
  rawValue: string;
  matchKey: string;
}

export interface ImportConflictRow {
  rowIndex: number;
  errorType: 'cross_row_duplicate' | 'conflicting_grouping';
  identifierType: string;
  identifierValue: string;
  matchKey: string;
  reason: string;
}

export interface ProposedRemap {
  identifierType: string;
  identifierValue: string;
  existingIdentifierValue: string;
  priorMasterProductId: string;
  priorVariantLabel: string;
  newMasterProductId: string | null;
  newVariantLabel: string;
  rowIndex: number;
}

export interface ProposedReplacement extends ProposedRemap {
  isRemap: boolean;
}

export interface RowAssignment {
  rowIndex: number;
  observations: MappingObservation[];
  existingMasterProductIds: string[];
  action: 'create' | 'update';
  targetMasterProductId: string | null;
  groupKey: string;
}

export type ClassifyOutcome =
  | {
      kind: 'ok';
      rowsScanned: number;
      assignments: RowAssignment[];
      remaps: ProposedRemap[];
      replacements: ProposedReplacement[];
      observations: MappingObservation[];
    }
  | {
      kind: 'blocking';
      rowsScanned: number;
      blockingRows: number;
      conflicts: ImportConflictRow[];
      message: string;
    };

/** Trim + case-fold for equality. Internal spaces are preserved (AC-170-08/09). */
export function toImportMatchKey(value: string): string {
  return value.trim().toLowerCase();
}

export function isBlankIdentifierCell(value: unknown): boolean {
  return typeof value !== 'string' || value.trim().length === 0;
}

export function parseMappingCsv(text: string): { headers: string[]; rows: MappingCsvRow[] } {
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter((line) => line.length > 0);
  if (lines.length === 0) {
    return { headers: [], rows: [] };
  }
  const headers = splitCsvLine(lines[0]);
  const rows: MappingCsvRow[] = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cells = splitCsvLine(lines[i]);
    const padded = headers.map((_, idx) => cells[idx] ?? '');
    rows.push({ cells: padded });
  }
  return { headers, rows };
}

export function normalizeMappingCsvRows(rows: unknown): MappingCsvRow[] {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    if (row && typeof row === 'object' && Array.isArray((row as MappingCsvRow).cells)) {
      return {
        cells: (row as MappingCsvRow).cells.map((cell) => (typeof cell === 'string' ? cell : '')),
      };
    }
    if (Array.isArray(row)) {
      return { cells: row.map((cell) => (typeof cell === 'string' ? cell : '')) };
    }
    return { cells: [] };
  });
}

export function normalizeIdentifierColumnIndexes(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<number>();
  const indexes: number[] = [];
  value.forEach((item) => {
    const index = typeof item === 'number' ? item : Number(item);
    if (!Number.isInteger(index) || index < 0 || seen.has(index)) return;
    seen.add(index);
    indexes.push(index);
  });
  return indexes;
}

function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      out.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  out.push(current);
  return out;
}

export function collectObservations(
  rows: MappingCsvRow[],
  headers: string[],
  identifierColumnIndexes: number[],
): MappingObservation[] {
  const observations: MappingObservation[] = [];
  rows.forEach((row, rowIndex) => {
    identifierColumnIndexes.forEach((columnIndex) => {
      if (columnIndex < 0 || columnIndex >= headers.length) return;
      const identifierType = headers[columnIndex] ?? '';
      if (identifierType.trim().length === 0) return;
      const raw = row.cells[columnIndex] ?? '';
      if (isBlankIdentifierCell(raw)) return;
      observations.push({
        rowIndex,
        identifierType,
        rawValue: raw,
        matchKey: toImportMatchKey(raw),
      });
    });
  });
  return observations;
}

function linkMatchKey(link: ExistingIdentifierLink): string {
  return toImportMatchKey(link.identifierValue);
}

/** Existing-link lookup: prefer type+value, else value-only (AC-170-08). */
function resolveExistingForObservation(
  observation: MappingObservation,
  existingLinks: ExistingIdentifierLink[],
): ExistingIdentifierLink | undefined {
  const sameType = existingLinks.find(
    (link) =>
      toImportMatchKey(link.identifierType) === toImportMatchKey(observation.identifierType) &&
      linkMatchKey(link) === observation.matchKey,
  );
  if (sameType) return sameType;
  return existingLinks.find((link) => linkMatchKey(link) === observation.matchKey);
}

export function classifyMappingCsvImport(input: {
  rows: MappingCsvRow[];
  headers: string[];
  identifierColumnIndexes: number[];
  existingLinks: ExistingIdentifierLink[];
}): ClassifyOutcome {
  const { rows, headers, identifierColumnIndexes, existingLinks } = input;
  const observations = collectObservations(rows, headers, identifierColumnIndexes);
  const rowsScanned = rows.length;

  const matchKeyRows = new Map<string, number[]>();
  observations.forEach((obs) => {
    const list = matchKeyRows.get(obs.matchKey) ?? [];
    if (!list.includes(obs.rowIndex)) list.push(obs.rowIndex);
    matchKeyRows.set(obs.matchKey, list);
  });

  const conflicts: ImportConflictRow[] = [];

  matchKeyRows.forEach((rowIndexes, matchKey) => {
    if (rowIndexes.length < 2) return;
    const sample = observations.find((obs) => obs.matchKey === matchKey);
    rowIndexes.forEach((rowIndex) => {
      const rowObs = observations.find((obs) => obs.matchKey === matchKey && obs.rowIndex === rowIndex);
      conflicts.push({
        rowIndex,
        errorType: 'cross_row_duplicate',
        identifierType: rowObs?.identifierType ?? sample?.identifierType ?? '',
        identifierValue: rowObs?.rawValue ?? sample?.rawValue ?? matchKey,
        matchKey,
        reason: `Identifier value appears in multiple rows (${rowIndexes.map((i) => i + 1).join(', ')})`,
      });
    });
  });

  if (conflicts.length > 0) {
    const blockingRows = new Set(conflicts.map((c) => c.rowIndex)).size;
    return {
      kind: 'blocking',
      rowsScanned,
      blockingRows,
      conflicts,
      message: 'No rows were imported.',
    };
  }

  const groupingConflicts = detectConflictingGroupings(observations, existingLinks);
  if (groupingConflicts.length > 0) {
    const blockingRows = new Set(groupingConflicts.map((c) => c.rowIndex)).size;
    return {
      kind: 'blocking',
      rowsScanned,
      blockingRows,
      conflicts: groupingConflicts,
      message: 'No rows were imported.',
    };
  }

  const byRow = new Map<number, MappingObservation[]>();
  observations.forEach((obs) => {
    const list = byRow.get(obs.rowIndex) ?? [];
    list.push(obs);
    byRow.set(obs.rowIndex, list);
  });

  const assignments: RowAssignment[] = [];
  const remaps: ProposedRemap[] = [];
  const replacements: ProposedReplacement[] = [];

  byRow.forEach((rowObs, rowIndex) => {
    const existingForRow: ExistingIdentifierLink[] = [];
    rowObs.forEach((obs) => {
      const found = resolveExistingForObservation(obs, existingLinks);
      if (found && !existingForRow.some((l) => l.masterProductId === found.masterProductId && linkMatchKey(l) === linkMatchKey(found))) {
        existingForRow.push(found);
      }
    });

    const uniqueMpIds = [...new Set(existingForRow.map((l) => l.masterProductId))];
    const action: 'create' | 'update' = uniqueMpIds.length === 0 ? 'create' : 'update';
    const targetMasterProductId = uniqueMpIds[0] ?? null;
    const newVariantLabel = targetMasterProductId
      ? existingForRow.find((l) => l.masterProductId === targetMasterProductId)?.displayName ?? targetMasterProductId
      : `new-variant-row-${rowIndex + 1}`;

    rowObs.forEach((obs) => {
      const found = resolveExistingForObservation(obs, existingLinks);
      if (!found) return;
      const isRemap = found.masterProductId !== targetMasterProductId;
      const replacement: ProposedReplacement = {
        identifierType: obs.identifierType,
        identifierValue: obs.rawValue,
        existingIdentifierValue: found.identifierValue,
        priorMasterProductId: found.masterProductId,
        priorVariantLabel: found.displayName ?? found.masterProductId,
        newMasterProductId: targetMasterProductId,
        newVariantLabel,
        rowIndex,
        isRemap,
      };
      replacements.push(replacement);
      if (isRemap) remaps.push(replacement);
    });

    assignments.push({
      rowIndex,
      observations: rowObs,
      existingMasterProductIds: uniqueMpIds,
      action,
      targetMasterProductId,
      groupKey: `row-${rowIndex}`,
    });
  });

  return {
    kind: 'ok',
    rowsScanned,
    assignments,
    remaps,
    replacements,
    observations,
  };
}

export function groupReplacementsByRow(
  replacements: ProposedReplacement[],
): Array<{ rowIndex: number; items: ProposedReplacement[] }> {
  const byRow = new Map<number, ProposedReplacement[]>();
  replacements.forEach((item) => {
    const list = byRow.get(item.rowIndex) ?? [];
    list.push(item);
    byRow.set(item.rowIndex, list);
  });
  return [...byRow.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([rowIndex, items]) => ({ rowIndex, items }));
}

/**
 * AC-170-14: two CSV rows that do not share a cell value still conflict when
 * existing same-variant links plus each row's extra mapped identifiers would
 * pull one existing Variant toward two different other Variants.
 */
function detectConflictingGroupings(
  observations: MappingObservation[],
  existingLinks: ExistingIdentifierLink[],
): ImportConflictRow[] {
  const byRow = new Map<number, MappingObservation[]>();
  observations.forEach((obs) => {
    const list = byRow.get(obs.rowIndex) ?? [];
    list.push(obs);
    byRow.set(obs.rowIndex, list);
  });

  const rowMpSets = new Map<number, Set<string>>();
  byRow.forEach((rowObs, rowIndex) => {
    const mps = new Set<string>();
    rowObs.forEach((obs) => {
      const found = resolveExistingForObservation(obs, existingLinks);
      if (found) mps.add(found.masterProductId);
    });
    rowMpSets.set(rowIndex, mps);
  });

  const mpToRows = new Map<string, number[]>();
  rowMpSets.forEach((mps, rowIndex) => {
    mps.forEach((mp) => {
      const list = mpToRows.get(mp) ?? [];
      if (!list.includes(rowIndex)) list.push(rowIndex);
      mpToRows.set(mp, list);
    });
  });

  const conflicts: ImportConflictRow[] = [];
  mpToRows.forEach((rowIndexes, sharedMp) => {
    if (rowIndexes.length < 2) return;
    const extraMps = new Set<string>();
    rowIndexes.forEach((rowIndex) => {
      rowMpSets.get(rowIndex)?.forEach((mp) => {
        if (mp !== sharedMp) extraMps.add(mp);
      });
    });
    if (extraMps.size < 2) return;

    rowIndexes.forEach((rowIndex) => {
      const rowObs = byRow.get(rowIndex) ?? [];
      const sample = rowObs[0];
      if (!sample) return;
      conflicts.push({
        rowIndex,
        errorType: 'conflicting_grouping',
        identifierType: sample.identifierType,
        identifierValue: sample.rawValue,
        matchKey: sample.matchKey,
        reason: `Rows ${rowIndexes.map((i) => i + 1).join(' and ')} imply different groupings for identifiers already on the same Variant`,
      });
    });
  });

  return conflicts;
}

export interface SuccessMetrics {
  rowsProcessed: number;
  newMasterProductsCreated: number;
  existingMasterProductsUpdated: number;
  identifierObservationsInserted: number;
  productLinksCreatedOrUpdated: number;
  noBlockingIssues: true;
}

export function buildSuccessMetrics(assignments: RowAssignment[]): SuccessMetrics {
  const rowsProcessed = assignments.length;
  const newMasterProductsCreated = assignments.filter((a) => a.action === 'create').length;
  const existingMasterProductsUpdated = assignments.filter((a) => a.action === 'update').length;
  const identifierObservationsInserted = assignments.reduce((sum, a) => sum + a.observations.length, 0);
  return {
    rowsProcessed,
    newMasterProductsCreated,
    existingMasterProductsUpdated,
    identifierObservationsInserted,
    productLinksCreatedOrUpdated: identifierObservationsInserted,
    noBlockingIssues: true,
  };
}

export function buildFailureSummary(outcome: Extract<ClassifyOutcome, { kind: 'blocking' }>) {
  return {
    rowsScanned: outcome.rowsScanned,
    blockingRows: outcome.blockingRows,
    errorTypeByRow: outcome.conflicts.map((c) => ({
      row: c.rowIndex + 1,
      errorType: c.errorType,
      identifierType: c.identifierType,
      identifierValue: c.identifierValue,
      reason: c.reason,
    })),
    message: outcome.message,
  };
}
